import { useTicketStore } from "../../Stores/Ticket";
import BotActionChatMessage from "../BotActionChatMessage/BotActionChatMessage";
import BotActionInputCalendly from "../BotActionInputCalendly/BotActionInputCalendly";
import { CaptureRequestCard } from "../CaptureRequestCard/CaptureRequestCard";
import ChatMessage from "../ChatMessage/ChatMessage";
import {
  hasCustomerAnsweredOpeningPrompt,
  isConversationOpenedByCustomer,
} from "../../Helper/AwaitingBotReply";
import {
  isCaptureLogsNotice,
  parseCaptureCardData,
} from "../../Helper/CaptureRequest";
import { isRedundantTailAskForMoreHelp } from "../../Helper/AskForMoreHelp";
import "./BotActionInput.scss";
import "./BotActionInputButtons.scss";
import {
  StepButtonsCard,
  StepInputCard,
  StepMultiCard,
  StepRatingCard,
  StepSelectCard,
  StepUploadCard,
} from "../ChatWidget/Cards/StepCards/StepCards";
import { VerifyEmailStepCard } from "../ChatWidget/Cards/StepCards/VerifyEmailStepCard";

/** The Verify email card with its line above it: the workflow step, and Kai's own card (Helper/KaiVerifyCard). */
export const VerifyEmailPrompt = ({ action }: { action: any }) => (
  <>
    {typeof action?.message === "string" &&
      action.message.trim().length > 0 && (
        <ChatMessage
          key="verifyemail-message"
          firstOfGroup={true}
          lastOfGroup={false}
          chatMessage={{
            data: { content: action.message },
            user: undefined,
            session: undefined,
            type: "BOT",
          }}
          showTime={false}
        />
      )}
    <VerifyEmailStepCard action={action} />
  </>
);

function BotActionInput() {
  const feedbackItemStore = useTicketStore();
  const currentAction = feedbackItemStore.currentBotAction?.action;
  if (!currentAction) {
    return null;
  }

  // Every workflow input is a chat card (StepCards); the payloads are the
  // ones the server has always taken for each type.
  const renderInputComponentForAction = () => {
    const attribute = currentAction?.attribute;
    if (!attribute?.type) {
      return currentAction.inputType === "dropdown" ? (
        <StepSelectCard action={currentAction} />
      ) : (
        <StepInputCard action={currentAction} />
      );
    }

    switch (attribute.type) {
      case "DROPDOWN":
      case "SELECT":
        return <StepSelectCard action={currentAction} />;
      case "MULTISELECT":
        return <StepMultiCard action={currentAction} />;
      case "UPLOAD":
        return <StepUploadCard action={currentAction} />;
      default:
        return <StepInputCard action={currentAction} />;
    }
  };

  const renderContent = () => {
    switch (currentAction.type) {
      case "rateconversation":
        return <StepRatingCard action={currentAction} />;
      case "input":
        return renderInputComponentForAction();
      case "verifyemail":
        return <VerifyEmailPrompt action={currentAction} />;
      case "calendly":
        // The step carries a booking card (`data.widget`) under its message:
        // the card is the UI, so no embed button here.
        if (currentAction?.bookingWidgetId) {
          return null;
        }
        return (
          <BotActionChatMessage date={currentAction?.createdAt ?? new Date()}>
            <BotActionInputCalendly action={currentAction} />
          </BotActionChatMessage>
        );
      case "capture": {
        // Workflow "Ask for screenshot / screen recording" step. The parked
        // comment itself stays hidden like every step; its card data and
        // message live on that comment (data.captureRequest / data.content),
        // with the step config as the fallback.
        const stepComment = feedbackItemStore.currentComments
          .slice()
          .reverse()
          .find(
            (comment) =>
              comment.data?.action?.type === "capture" &&
              (!currentAction.captureRequestId ||
                comment.data?.captureRequest?.id ===
                  currentAction.captureRequestId ||
                comment.data?.action?.captureRequestId ===
                  currentAction.captureRequestId),
          );
        const card = parseCaptureCardData(stepComment?.data?.captureRequest, {
          id: currentAction.captureRequestId,
          kind: currentAction.captureKind,
          allowUpload: currentAction.allowUpload,
          annotate: currentAction.annotate,
          maxDurationSec: currentAction.maxDurationSec,
          audio: currentAction.audio,
        });
        if (!card) {
          return null;
        }
        const content = stepComment?.data?.content;
        const message =
          typeof content === "string"
            ? content
            : typeof currentAction.message === "string"
              ? currentAction.message
              : undefined;
        return (
          <BotActionChatMessage date={currentAction?.createdAt ?? new Date()}>
            <CaptureRequestCard
              card={card}
              message={message}
              variant="step"
              ticketShareToken={feedbackItemStore.currentTicket?.shareToken}
            />
          </BotActionChatMessage>
        );
      }
      case "answerbotflow":
        const answerBotReply = () => {
          if (feedbackItemStore.hasAnswerBotReply()) {
            return (
              <>
                {/* A blank line (e.g. a single space, set to switch the
                    follow-up off) would render as an empty bubble. */}
                {typeof currentAction.askForAdditionalHelp === "string" &&
                  currentAction.askForAdditionalHelp.trim().length > 0 &&
                  !isRedundantTailAskForMoreHelp(
                    feedbackItemStore.currentComments,
                  ) && (
                    <ChatMessage
                      key="answerbot"
                      firstOfGroup={true}
                      lastOfGroup={true}
                      chatMessage={{
                        data: {
                          content: currentAction.askForAdditionalHelp ?? "",
                          kai: true,
                        },
                        user: undefined,
                        session: undefined,
                        type: "BOT",
                      }}
                      showTime={true}
                    />
                  )}
              </>
            );
          }

          // Only render the opening question/greeting while it's still the
          // unanswered tail (the active prompt). Once the customer has replied,
          // the greeting lives in the timeline (ConversationMessageList) so it
          // persists there instead of disappearing the moment the message posts.
          const comments = feedbackItemStore.currentComments;

          // The customer opened this conversation with their own question
          // (home ask-input): the opening greeting makes no sense — they
          // started the conversation, the bot didn't. The server suppresses
          // the greeting comment; this suppresses the transient client-side
          // render of the same text while the action is the unanswered tail.
          // The mirrored question covers the moments before the thread loads;
          // the thread covers a question the server already put first (a
          // workflow that collected data before its AI step, or a reload).
          const customerOpened =
            !!feedbackItemStore.pendingAiQuestionText ||
            isConversationOpenedByCustomer(comments);
          const tailComments = (comments ?? []).filter(
            (comment) => !isCaptureLogsNotice(comment),
          );
          const lastComment = tailComments[tailComments.length - 1];
          const promptIsTail = !!(
            lastComment?.type === "BOT" && lastComment?.data?.action
          );
          // A reply to the opening means it already lives in the timeline: a
          // later trailing action (it carries the same question) must not show
          // it again under Kai's newest message. Kai intake's capture request
          // is such a turn without an `answerBot` comment. A button tap that
          // led into this step is no such reply: the question still shows.
          if (
            !customerOpened &&
            promptIsTail &&
            !hasCustomerAnsweredOpeningPrompt(comments) &&
            currentAction?.question?.trim?.().length > 0
          ) {
            return (
              <ChatMessage
                key="answerbot"
                firstOfGroup={true}
                lastOfGroup={true}
                chatMessage={{
                  data: {
                    content: currentAction?.question ?? "",
                  },
                  user: undefined,
                  session: undefined,
                  type: "BOT",
                }}
                showTime={true}
              />
            );
          }

          return <></>;
        };

        return (
          <>
            {answerBotReply()}
            <div className="bot-buttons">
              {currentAction.capabilities
                ?.filter((capability: any) => {
                  if (!capability?.showButton) return false;

                  const lastMessageIsAIAnswer =
                    feedbackItemStore.lastMessageIsAIAnswer();
                  const contextMissing =
                    feedbackItemStore.lastMessageHasContextAnswer();

                  if (capability?.showButtonAlwaysWhenNoAnswer) {
                    if (lastMessageIsAIAnswer) {
                      if (contextMissing) {
                        return true;
                      }
                    }
                  }

                  if (capability?.showButtonOnlyWhenNoAnswer) {
                    if (lastMessageIsAIAnswer || !contextMissing) {
                      return false;
                    }
                  }

                  // Get the current bot response count
                  const responseCount = feedbackItemStore.getBotResponseCount();

                  // Show the button if:
                  // 1. showButtonAfterResponses is not set (null or undefined), or
                  // 2. The bot response count matches showButtonAfterResponses
                  return (
                    responseCount >= (capability?.showButtonAfterResponses ?? 1)
                  );
                })
                .map((capability: any, index: number) => {
                  return (
                    <button
                      key={index}
                      className="bot-button"
                      onClick={() => {
                        feedbackItemStore.answerBotAction(
                          {
                            action: capability?.id,
                            message: capability?.buttonText,
                          },
                          [],
                        );
                      }}
                    >
                      {capability?.buttonText}
                    </button>
                  );
                })}
            </div>
          </>
        );
      case "buttons":
        return <StepButtonsCard action={currentAction} />;
      default:
        return null;
    }
  };

  return <div className="bot-action-container">{renderContent()}</div>;
}

export default BotActionInput;
